// BotConversations.tsx — the Conversations screen (spec D3/M5 task-2, handoff §8). 320px
// date-grouped list + a 66vh transcript pane. The list never auto-refreshes (D3: "polls
// nothing"); "Load more" appends the next keyset page instead of infinite scroll. Bubbles
// reuse the same components as Playground (`components/chat-bubbles.tsx`).
//
// Deletion (hygiene-cycle spec §B) — a live compliance gap against the Privacy page's
// deletion promise: a delete control in the transcript pane header for the OPEN conversation
// (simple confirm — a single transcript isn't destructive enough to warrant type-to-confirm)
// + "Delete all conversations" in the list header (type-to-confirm on the bot's name, same
// friction as BotSettings' DangerZone bot-delete dialog and BotLeads' "Delete all leads").
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Button } from '@repo/ui/components/button';
import { EmptyState } from '@repo/ui/components/empty-state';
import { Input } from '@repo/ui/components/input';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip } from '@repo/ui/components/status-chip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { MessageCircleIcon, Trash2Icon } from 'lucide-react';
import type { BotDto, ConversationListItemDto } from '@repo/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader';
import { AssistantBubble, UserBubble } from '../components/chat-bubbles';
import { useConversations, useConversationTranscript } from '../hooks/useConversations';
import { apiFetch } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useLocale, useT } from '../locales/context';
import type { Catalog } from '../locales/en';
import { formatDate } from '../locales/format';
import type { Locale } from '../locales/index';

/** Compact "12m"/"1h"/"3h"/"1d" style used for list row timestamps (matches the same
 * compact format `BotOverview`'s "Latest conversations" card uses). */
function compactRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

/** "Today" / "Yesterday" / "Aug 2" — grouped by LOCAL calendar day of `lastMessageAt`
 * (spec D3/handoff §8: date group labels, uppercase 11.5px per the Table-header rule). */
function dateGroupLabel(iso: string, locale: Locale, t: Catalog): string {
  const date = new Date(iso);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (diffDays === 0) return t.conversations.today;
  if (diffDays === 1) return t.conversations.yesterday;
  return formatDate(date, locale, { month: 'short', day: 'numeric' });
}

/** Groups the already-sorted (lastMessageAt desc) list into consecutive same-label runs —
 * this does NOT re-sort, so a resumed old conversation jumping back to the top of the list
 * can in principle open a second "Today" group further down; an accepted edge case rather
 * than something worth a second sort pass. */
function groupByDate(
  items: ConversationListItemDto[],
  locale: Locale,
  t: Catalog,
): { label: string; items: ConversationListItemDto[] }[] {
  const groups: { label: string; items: ConversationListItemDto[] }[] = [];
  for (const item of items) {
    const label = dateGroupLabel(item.lastMessageAt, locale, t);
    const last = groups.at(-1);
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

const DATE_GROUP_LABEL_CLASS =
  'px-4 pt-3 pb-2 text-[11.5px] font-semibold tracking-[0.04em] text-muted-foreground uppercase';

/** Sized to the real list-row shape below (handoff Shell Anatomy §06) — each row is the same
 * height a loaded row occupies, so the list doesn't grow once data lands. */
function ListSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[14px] border bg-card">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={`flex flex-col gap-1.5 px-4 py-2.5 ${i === 0 ? '' : 'border-t'}`}>
          <div className="flex items-center justify-between gap-2">
            <SkeletonText className="w-14" />
            <SkeletonText className="w-8" />
          </div>
          <SkeletonText className="w-full" />
        </div>
      ))}
    </div>
  );
}

/** Sized to a handful of chat bubbles (`chat-bubbles.tsx`) at their real max-width, so the
 * transcript pane doesn't jump once the real messages render. */
function TranscriptSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
      <div className="flex justify-end">
        <Skeleton className="h-8 w-[55%] rounded-2xl rounded-br-[6px]" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-8 w-[70%] rounded-2xl rounded-bl-[6px]" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-8 w-[40%] rounded-2xl rounded-br-[6px]" />
      </div>
    </div>
  );
}

/**
 * List + transcript pane, holding the `selectedId` selection state. Rendered by
 * `BotConversations` with `key={deepLinkId}` — a `?open=` deep-link (used by Leads/
 * Unanswered's "View chat") needs to (re)select a row whenever it changes, INCLUDING while
 * this screen is already mounted (a second "View chat" click, or browser back/forward), not
 * just once at initial mount. Syncing that via a `useEffect(() => setSelectedId(deepLinkId),
 * [deepLinkId])` trips `react-hooks/set-state-in-effect` (cascading render) — keying this
 * subtree on `deepLinkId` instead makes React remount it with a FRESH `useState(deepLinkId)`
 * whenever the deep-link id changes, with no effect needed. Between deep-link changes,
 * selection is pure user interaction: clicking a row calls local `setSelectedId` and does
 * NOT touch the URL, so no remount happens and the manual selection sticks.
 */
function ConversationsPane({
  botId,
  deepLinkId,
  groups,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  botId: string | undefined;
  deepLinkId: string | null;
  groups: { label: string; items: ConversationListItemDto[] }[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(deepLinkId);
  const {
    data: transcript,
    isPending: transcriptPending,
    error: transcriptError,
  } = useConversationTranscript(selectedId);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteConversationMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>(`/api/bots/${botId}/conversations/${selectedId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      // Fix-wave item 3: a deleted conversation cascades its messages away, which moves
      // `totals.messages14d`, the sparkline, `latestConversations`, AND `totals.unansweredGroups`
      // (the rail's amber pill) on Overview — same pattern `useAnswerUnanswered` uses.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bots', botId, 'conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] }),
      ]);
      setDeleteOpen(false);
      setSelectedId(null);
    },
  });

  return (
    // <1024px: one pane at a time (list, then transcript-with-back-button) — the 320px
    // list + transcript grid is desktop-only real estate; below that it's narrower than
    // most phones. ≥1024px keeps the original side-by-side grid.
    <div className="flex h-[70vh] flex-col gap-4 lg:h-[66vh] lg:grid lg:grid-cols-[320px_1fr]">
      <div
        className={`h-full flex-col overflow-y-auto rounded-[14px] border bg-card lg:flex ${
          selectedId ? 'hidden' : 'flex'
        }`}
      >
        {groups.map((group) => (
          <div key={group.label + group.items[0]!.id}>
            <div className={DATE_GROUP_LABEL_CLASS}>{group.label}</div>
            {group.items.map((conv) => {
              const isActive = conv.id === selectedId;
              return (
                <button
                  key={conv.id}
                  type="button"
                  onClick={() => setSelectedId(conv.id)}
                  className={`flex w-full flex-col gap-0.5 border-l-2 px-4 py-2.5 text-left ${
                    isActive ? '' : 'hover:bg-accent'
                  }`}
                  style={{
                    borderLeftColor: isActive ? 'var(--primary)' : 'transparent',
                    backgroundColor: isActive
                      ? 'color-mix(in srgb, var(--primary) var(--st-wash), transparent)'
                      : undefined,
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-[13px] font-medium">
                      {conv.channel === 'playground' ? t.conversations.yourTesting : t.conversations.visitor}
                      {conv.channel === 'playground' && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                          {t.conversations.playgroundTag}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {compactRelativeTime(conv.lastMessageAt)}
                    </span>
                  </div>
                  <span className="overflow-hidden text-[12.5px] text-ellipsis whitespace-nowrap text-muted-foreground">
                    {conv.snippet || t.conversations.noMessageFallback}
                  </span>
                  {conv.leadCaptured && (
                    <span className="text-[11.5px] font-medium text-st-ready">{t.conversations.leadCapturedTag}</span>
                  )}
                  {conv.hasUnanswered && (
                    <span className="text-[11.5px] font-medium text-st-paused">{t.conversations.hasUnansweredTag}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
        {hasNextPage && (
          <div className="p-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? t.conversations.loadingMore : t.conversations.loadMore}
            </Button>
          </div>
        )}
      </div>

      <div
        className={`h-full flex-col overflow-hidden rounded-[14px] border bg-card lg:flex ${
          selectedId ? 'flex' : 'hidden'
        }`}
      >
        {selectedId && (
          <div className="border-b p-2 lg:hidden">
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {t.conversations.backToConversations}
            </button>
          </div>
        )}
        {!selectedId && (
          <div className="flex flex-1 items-center justify-center p-10 text-center">
            <p className="text-sm text-muted-foreground">
              {t.conversations.selectConversationPrompt}
            </p>
          </div>
        )}
        {selectedId && transcriptPending && <TranscriptSkeleton />}
        {/* Fix-wave item 6 — `useConversationTranscript` had no error branch, so a failed
            fetch left `transcript` `undefined` forever and this pane just rendered blank (no
            skeleton match below, no message) once `transcriptPending` settled false. Same
            three-state shape (pending → skeleton; error → visible message; resolved → render)
            item 1 applies to the shell. */}
        {selectedId && !transcriptPending && transcriptError && (
          <div className="flex flex-1 items-center justify-center p-10 text-center" role="alert">
            <p className="text-sm text-destructive">{transcriptError.message}</p>
          </div>
        )}
        {selectedId && !transcriptPending && transcript && (
          <>
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div className="flex flex-col">
                <span className="flex items-center gap-1.5 text-[13.5px] font-semibold">
                  {transcript.channel === 'playground' ? t.conversations.yourTesting : t.conversations.visitor}
                  {transcript.channel === 'playground' && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                      {t.conversations.playgroundTag}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t.conversations.transcriptStartedSummary({
                    time: relativeTime(transcript.startedAt, t.time),
                    n: transcript.messages.length,
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {transcript.lead && <StatusChip state="ready" label={t.conversations.leadCapturedTag} />}
                <button
                  type="button"
                  aria-label={t.conversations.deleteConversationAriaLabel}
                  onClick={() => {
                    deleteConversationMutation.reset();
                    setDeleteOpen(true);
                  }}
                  className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
                >
                  <Trash2Icon className="size-4" />
                </button>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
              {transcript.messages.map((message) =>
                message.role === 'user' ? (
                  <UserBubble key={message.id} text={message.content} />
                ) : (
                  <AssistantBubble
                    key={message.id}
                    text={message.content}
                    citations={message.citations}
                    unanswered={message.unanswered}
                  />
                ),
              )}
              {/* The DTO carries only the captured lead, not when it happened relative to
                  the messages, so this card renders at the end of the transcript rather
                  than inline at its exact turn (handoff §8 shows it inline in a mock —
                  there's no per-message ordering signal to place it precisely). */}
              {transcript.lead && (
                <div className="flex flex-col gap-0.5 self-start rounded-[10px] border bg-background px-3.5 py-2.5 text-[12.5px]">
                  <span className="font-semibold text-st-ready">{t.conversations.leadSubmittedTag}</span>
                  <span>
                    {[transcript.lead.name, transcript.lead.email, transcript.lead.phone]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.conversations.deleteDialogTitle}</DialogTitle>
            <DialogDescription>{t.conversations.deleteDialogDescription}</DialogDescription>
          </DialogHeader>
          {deleteConversationMutation.isError && (
            <p className="text-[13px] text-destructive">{deleteConversationMutation.error.message}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)}>
              {t.conversations.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteConversationMutation.isPending}
              onClick={() => deleteConversationMutation.mutate()}
            >
              {deleteConversationMutation.isPending ? t.conversations.deletingCta : t.conversations.deleteCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BotConversations() {
  const { botId } = useParams<{ botId: string }>();
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get('open');
  const queryClient = useQueryClient();
  const { locale, t } = useLocale();

  const { data: bot } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });

  const { data, isPending, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useConversations(botId);
  const items = data?.pages.flatMap((p) => p.items) ?? [];

  const groups = groupByDate(items, locale, t);

  // "Delete all conversations" (list header) — type-to-confirm on the bot's name, same
  // pattern as BotLeads/BotSettings. `resetKey` forces `ConversationsPane` to remount (fresh
  // `selectedId` state) after a bulk delete, the same way a deep-link change does, so a
  // transcript that no longer exists never lingers on screen.
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const deleteAllMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: number }>(`/api/bots/${botId}/conversations`, { method: 'DELETE' }),
    onSuccess: async () => {
      // Fix-wave item 3: same reasoning as the single-conversation delete above — deleting
      // every conversation moves Overview's message totals, sparkline, latest list, and the
      // rail's unanswered pill all at once.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bots', botId, 'conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] }),
      ]);
      setDeleteAllOpen(false);
      setResetKey((k) => k + 1);
    },
  });

  function openDeleteAll() {
    deleteAllMutation.reset();
    setConfirmName('');
    setDeleteAllOpen(true);
  }

  // Task 7 header subline: the list's own count, same fact the "X conversations · newest
  // first" row used to carry — folded into the header instead of repeated below it.
  const headerSubtitle =
    !isPending && !error ? t.conversations.headerSubtitle({ n: items.length }) : undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t.conversations.pageTitle} subtitle={headerSubtitle} />

      {isPending && items.length === 0 && <ListSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {items.length === 0 && !isPending && !error ? (
        <EmptyState
          icon={MessageCircleIcon}
          title={t.conversations.emptyTitle}
          description={t.conversations.emptyDescription}
        />
      ) : (
        <>
          {bot && (
            <div className="flex justify-end">
              <Button type="button" variant="destructive-soft" size="sm" onClick={openDeleteAll}>
                {t.conversations.deleteAllCta}
              </Button>
            </div>
          )}
          <ConversationsPane
            key={`${deepLinkId ?? 'no-deep-link'}:${resetKey}`}
            botId={botId}
            deepLinkId={deepLinkId}
            groups={groups}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={() => void fetchNextPage()}
          />
        </>
      )}

      <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.conversations.deleteAllDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.conversations.deleteAllDescriptionPrefix}
              <span className="font-medium text-foreground">{bot?.name}</span>
              {t.conversations.deleteAllDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label={t.conversations.confirmBotNameAriaLabel}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={bot?.name}
            autoFocus
          />
          {deleteAllMutation.isError && (
            <p className="text-[13px] text-destructive">{deleteAllMutation.error.message}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteAllOpen(false)}>
              {t.conversations.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={confirmName !== bot?.name || deleteAllMutation.isPending}
              onClick={() => deleteAllMutation.mutate()}
            >
              {deleteAllMutation.isPending ? t.conversations.deletingCta : t.conversations.deleteAllCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
